// src/pages/Calibration/CalibrationEventForm.jsx
//
// AISC §1.14 Calibration Phase 1 — record a new calibration event.
//
// Redesigned 2026-05-05 (Phase 2.2, Class P) onto the PaperForm helper
// library. AISC §2.7.2 NIST-traceable calibration event capture.

import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link as RouterLink } from "react-router-dom";
import {
  Stack, Typography, Breadcrumbs, Box, Button, Alert, Grid,
  TextField, RadioGroup, Radio, FormControlLabel, CircularProgress,
  IconButton,
} from "@mui/material";
import { Helmet } from "react-helmet-async";
import { toast } from "react-toastify";
import { ArrowBackRounded, ScienceOutlined } from "@mui/icons-material";

import { useSelector } from "react-redux";
import { calibrationUnitsApi, calibrationEventsApi } from "../../utils/calibrationApi";
import { hasPermission, PERMISSIONS } from "../../utils/hasPermission";
import {
  PaperFormCard, PaperFormHeader, PaperFormSection, PaperFormFooter,
  FieldLabel,
} from "../../components/PaperForm";

export default function CalibrationEventForm() {
  const { unitId } = useParams();
  const navigate = useNavigate();
  // S-71 (2026-10-07): recording a calibration event needs manage_calibration (Admin, Quality
  // Manager, Quality Control Inspector). The server refuses it without (403); this page says so
  // instead of showing a form that cannot be saved.
  const { loginUser } = useSelector((state) => state.userSlice);
  const canManageCalibration = hasPermission(loginUser?.decodedToken?.permissions || [], PERMISSIONS.MANAGE_CALIBRATION);

  const [unit, setUnit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const [eventDate, setEventDate] = useState(new Date().toISOString().slice(0, 16));
  const [result, setResult] = useState("pass");
  const [method, setMethod] = useState("in_house");
  const [externalLabName, setExternalLabName] = useState("");
  const [externalLabCofc, setExternalLabCofc] = useState("");
  const [toleranceActual, setToleranceActual] = useState("");
  const [toleranceLimit, setToleranceLimit] = useState("");
  const [cofcDocumentId, setCofcDocumentId] = useState("");
  const [notes, setNotes] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const u = await calibrationUnitsApi.get(unitId);
        setUnit(u?.data?.data || null);
        if (u?.data?.data?.instrument?.requires_external_lab) {
          setMethod("external_lab");
        }
      } catch (err) {
        setError(err?.response?.data?.error || err.message);
      } finally { setLoading(false); }
    })();
  }, [unitId]);

  async function handleSubmit(e) {
    e.preventDefault();
    setError(null);
    if (!eventDate) { setError("Event Date is required."); return; }
    if (method === "external_lab" && !externalLabName.trim()) {
      setError("External Lab Name is required when method is External Lab.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        event_date: new Date(eventDate).toISOString(),
        result,
        method,
        external_lab_name: method === "external_lab" ? externalLabName : null,
        external_lab_cofc_number: externalLabCofc || null,
        tolerance_actual: toleranceActual || null,
        tolerance_limit: toleranceLimit || null,
        cofc_document_id: cofcDocumentId ? parseInt(cofcDocumentId, 10) : null,
        notes: notes || null,
      };
      await calibrationEventsApi.recordForUnit(unitId, payload);
      toast.success(`Calibration event recorded (${result})`);
      navigate(`/calibration/units/${unitId}`);
    } catch (err) {
      const msg = err?.response?.data?.error || err.message;
      setError(msg);
      toast.error(msg);
    } finally { setSaving(false); }
  }

  const eventDateDisplay = useMemo(() => {
    if (!eventDate) return "";
    try { return new Date(eventDate).toLocaleDateString(); } catch { return eventDate; }
  }, [eventDate]);

  if (!canManageCalibration) {
    return (
      <Box sx={{ p: 3, maxWidth: "md", mx: "auto" }}>
        <Alert severity="warning">You do not have permission to record calibration events.</Alert>
        <Button sx={{ mt: 2 }} onClick={() => navigate(`/calibration/units/${unitId}`)}>Back to the instrument</Button>
      </Box>
    );
  }
  if (loading) {
    return (
      <Box sx={{ pt: 6, textAlign: "center" }}>
        <CircularProgress />
      </Box>
    );
  }
  if (!unit) {
    return (
      <Box sx={{ p: 3, maxWidth: "md", mx: "auto" }}>
        <Alert severity="error">Instrument unit not found.</Alert>
      </Box>
    );
  }

  return (
    <>
      <Helmet><title>Record Calibration | SFM</title></Helmet>

      <Box sx={{ bgcolor: "#fafafa", minHeight: "100vh" }}>
        <Box sx={{ maxWidth: "md", mx: "auto", pt: 3, px: { xs: 2, md: 3 } }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
            <Breadcrumbs>
              <RouterLink to="/dashboard/app" style={{ color: "inherit", textDecoration: "none" }}>Home</RouterLink>
              <RouterLink to="/calibration" style={{ color: "inherit", textDecoration: "none" }}>Calibration</RouterLink>
              <RouterLink to={`/calibration/units/${unitId}`} style={{ color: "inherit", textDecoration: "none" }}>
                {unit.serial_number}
              </RouterLink>
              <Typography color="text.primary">New Event</Typography>
            </Breadcrumbs>
            <IconButton onClick={() => navigate(`/calibration/units/${unitId}`)} aria-label="Back">
              <ArrowBackRounded />
            </IconButton>
          </Stack>

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          <Alert severity="info" sx={{ mb: 2 }}>
            <strong>{unit.instrument?.name || "Instrument"}</strong>
            {" "}— Serial {unit.serial_number}
            {unit.make || unit.model ? ` — ${[unit.make, unit.model].filter(Boolean).join(" / ")}` : ""}
            {" · "}{unit.frequency_days_override || unit.instrument?.default_frequency_days || "—"} day cycle
            {unit.instrument?.requires_external_lab && " · External lab required (AISC §2.7.2)"}
          </Alert>
        </Box>

        <PaperFormCard>
          <PaperFormHeader
            formId="SFM · Calibration Event"
            title="Record Calibration Event"
            subtitle="AISC §2.7.2 NIST-traceable calibration record"
            status={result === "pass" ? "PASS" : "FAIL"}
            number={unit.serial_number || "—"}
            numberLabel="Serial No."
            date={eventDateDisplay}
            icon="science"
          />

          <Box component="form" onSubmit={handleSubmit}>
            {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

            <Grid container spacing={4} sx={{ mb: 4 }}>
              <Grid item xs={12} md={6}>
                <PaperFormSection label="Event" bottomPadding={0}>
                  <Box>
                    <FieldLabel required>Event Date</FieldLabel>
                    <TextField
                      fullWidth size="small" type="datetime-local"
                      value={eventDate}
                      onChange={(e) => setEventDate(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Box>
                  <Box>
                    <FieldLabel hint="pass = within tolerance · fail = out of tolerance">
                      Result
                    </FieldLabel>
                    <RadioGroup
                      row
                      value={result}
                      onChange={(e) => setResult(e.target.value)}
                    >
                      <FormControlLabel value="pass" control={<Radio size="small" />} label="Pass" />
                      <FormControlLabel value="fail" control={<Radio size="small" />} label="Fail (out of tolerance)" />
                    </RadioGroup>
                  </Box>
                  <Box>
                    <FieldLabel hint="external lab required for high-cap instruments">
                      Method
                    </FieldLabel>
                    <RadioGroup
                      row
                      value={method}
                      onChange={(e) => setMethod(e.target.value)}
                    >
                      <FormControlLabel value="in_house" control={<Radio size="small" />} label="In-house" />
                      <FormControlLabel value="external_lab" control={<Radio size="small" />} label="External lab" />
                    </RadioGroup>
                  </Box>
                </PaperFormSection>
              </Grid>

              <Grid item xs={12} md={6}>
                <PaperFormSection label="Tolerance & Evidence" bottomPadding={0}>
                  <Box>
                    <FieldLabel hint='free-text · e.g., "±27% at 5/8″ bolts"'>
                      Tolerance Actual
                    </FieldLabel>
                    <TextField
                      fullWidth size="small"
                      value={toleranceActual}
                      onChange={(e) => setToleranceActual(e.target.value)}
                      placeholder='e.g., ±27% at 5/8" bolts'
                    />
                  </Box>
                  <Box>
                    <FieldLabel hint='allowable · e.g., "±30%"'>Tolerance Limit</FieldLabel>
                    <TextField
                      fullWidth size="small"
                      value={toleranceLimit}
                      onChange={(e) => setToleranceLimit(e.target.value)}
                      placeholder="e.g., ±30%"
                    />
                  </Box>
                  <Box>
                    <FieldLabel hint="paste from Documents Library">
                      CofC Document ID
                    </FieldLabel>
                    <TextField
                      fullWidth size="small" type="number"
                      value={cofcDocumentId}
                      onChange={(e) => setCofcDocumentId(e.target.value)}
                      placeholder="e.g., 142"
                      helperText="Paste the instrument ID."
                    />
                  </Box>
                </PaperFormSection>
              </Grid>
            </Grid>

            {method === "external_lab" && (
              <PaperFormSection label="External Lab" hint="required when method = external lab">
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <FieldLabel required>External Lab Name</FieldLabel>
                    <TextField
                      fullWidth size="small"
                      value={externalLabName}
                      onChange={(e) => setExternalLabName(e.target.value)}
                      placeholder="e.g., Acme Calibration Services"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FieldLabel hint="lab's certificate-of-calibration number">
                      Lab CofC Number
                    </FieldLabel>
                    <TextField
                      fullWidth size="small"
                      value={externalLabCofc}
                      onChange={(e) => setExternalLabCofc(e.target.value)}
                      placeholder="e.g., CAL-2026-00428"
                    />
                  </Grid>
                </Grid>
              </PaperFormSection>
            )}

            <PaperFormSection label="Notes" hint="optional · context, anomalies, follow-ups">
              <TextField
                fullWidth multiline rows={3} size="small"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="e.g., Borrowed reference standard from QA Lab #2; recheck due in 30 days"
              />
            </PaperFormSection>

            <PaperFormFooter
              citation="AISC §2.7.2 — NIST-traceable calibration of inspection equipment"
            />

            <Stack direction="row" justifyContent="flex-end" spacing={1} sx={{ mt: 4 }}>
              <Button
                variant="text"
                onClick={() => navigate(`/calibration/units/${unitId}`)}
                disabled={saving}
                sx={{ color: "text.secondary" }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                startIcon={<ScienceOutlined />}
                disabled={saving}
                sx={{
                  px: 3,
                  borderRadius: 2,
                  textTransform: "none",
                  fontWeight: 600,
                }}
              >
                {saving ? "Submitting…" : "Record Calibration Event"}
              </Button>
            </Stack>
          </Box>
        </PaperFormCard>
      </Box>
    </>
  );
}
